import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, ArrowDown, Loader2 } from 'lucide-react';
import { artistsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function RevenuePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutId, setPayoutId] = useState('');
  const [payoutAmt, setPayoutAmt] = useState('');
  const [paying, setPaying] = useState(false);

  const fetchData = async () => {
    try { const r = await artistsAPI.revenueDashboard(); setData(r.data); } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePayout = async (projectId) => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt < 1) { toast.error('Min $1'); return; }
    setPaying(true);
    try {
      const r = await artistsAPI.requestPayout({ project_id: projectId, amount: amt });
      toast.success(r.data.message);
      setPayoutAmt('');
      setPayoutId('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payout failed'); }
    finally { setPaying(false); }
  };

  if (!user) return null;
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-2 border-[#00aa55] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="revenue-page">
      <div className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-6">
          <Link to="/" className="text-zinc-400 hover:text-zinc-700 mb-4 inline-flex items-center gap-1 text-sm" data-testid="revenue-back-home-link"><ArrowLeft size={16} /> Home</Link>
          <h1 className="font-display font-bold text-3xl text-zinc-900 mb-2">Revenue & Payouts</h1>
          <p className="text-zinc-500 text-sm">Your earnings from projects, events, and tips. 5% platform fee.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 mt-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Gross Revenue', value: formatCurrency(data?.gross_revenue || 0), color: 'text-zinc-900', sub: `Projects: ${formatCurrency(data?.project_revenue || 0)} | Events: ${formatCurrency(data?.ticket_revenue || 0)}` },
            { label: 'Platform Fee (5%)', value: formatCurrency(data?.platform_fee || 0), color: 'text-red-500', sub: '' },
            { label: 'Net Revenue', value: formatCurrency(data?.net_revenue || 0), color: 'text-[#00aa55]', sub: '' },
            { label: 'Available', value: formatCurrency(data?.available_balance || 0), color: 'text-[#00aa55]', sub: `Paid out: ${formatCurrency(data?.total_paid_out || 0)}` },
          ].map(s => (
            <div key={s.label} className="p-4 bg-white rounded-2xl border border-zinc-200">
              <p className="text-xs text-zinc-500 mb-1">{s.label}</p>
              <p className={`font-display font-bold text-2xl ${s.color}`}>{s.value}</p>
              {s.sub && <p className="text-[10px] text-zinc-400 mt-1">{s.sub}</p>}
            </div>
          ))}
        </div>

        {/* Projects with payout */}
        <h2 className="font-bold text-zinc-900 text-lg mb-4">Projects</h2>
        <div className="space-y-3 mb-8">
          {data?.projects?.map(p => {
            const net = p.funding * 0.95;
            const paidAlready = data.payouts?.filter(po => po.project_id === p.id).reduce((s, po) => s + po.amount, 0) || 0;
            const avail = Math.max(0, net - paidAlready);
            return (
              <div key={p.id} className="p-4 bg-white rounded-2xl border border-zinc-200" data-testid={`revenue-project-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <Link to={`/projects/${p.id}`} className="font-semibold text-zinc-900 hover:text-[#00aa55]">{p.title}</Link>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.status === 'funded' ? 'bg-green-100 text-green-600' : p.status === 'active' ? 'bg-blue-100 text-blue-600' : 'bg-zinc-100 text-zinc-500'}`}>{p.status}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
                  <span>Raised: {formatCurrency(p.funding)}</span>
                  <span>Net: {formatCurrency(net)}</span>
                  <span>Paid: {formatCurrency(paidAlready)}</span>
                  <span className="text-[#00aa55] font-bold">Available: {formatCurrency(avail)}</span>
                </div>
                {avail > 0 && (
                  <div className="flex gap-2">
                    {payoutId === p.id ? (
                      <>
                        <Input type="number" min={1} max={avail} value={payoutAmt} onChange={e => setPayoutAmt(e.target.value)} placeholder={`Max $${avail.toFixed(2)}`} className="w-40 text-sm border-zinc-200" data-testid="payout-amount-input" />
                        <Button onClick={() => handlePayout(p.id)} disabled={paying} className="bg-[#00aa55] text-white font-bold text-sm hover:bg-[#008844]" data-testid="confirm-payout-btn">
                          {paying ? <Loader2 size={14} className="animate-spin" /> : <><ArrowDown size={14} className="mr-1" /> Withdraw</>}
                        </Button>
                        <Button onClick={() => setPayoutId('')} variant="outline" className="text-sm border-zinc-200" data-testid="cancel-payout-btn">Cancel</Button>
                      </>
                    ) : (
                      <Button onClick={() => setPayoutId(p.id)} className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 text-sm" data-testid={`payout-btn-${p.id}`}>
                        <DollarSign size={14} className="mr-1" /> Withdraw to Wallet
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Payout History */}
        {data?.payouts?.length > 0 && (
          <>
            <h2 className="font-bold text-zinc-900 text-lg mb-4">Payout History</h2>
            <div className="space-y-2">
              {data.payouts.map(p => (
                <div key={p.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-zinc-200">
                  <div>
                    <span className="font-semibold text-zinc-800 text-sm">{p.project_title}</span>
                    <p className="text-xs text-zinc-400">{new Date(p.created_at).toLocaleDateString()} via {p.method}</p>
                  </div>
                  <span className="font-bold text-[#00aa55]">{formatCurrency(p.amount)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
